import React, { useState, useEffect, useRef } from 'react';
import { useModalCallback } from '../../contexts/ModalCallbackContext';
import { fetchLookup, createTimeSheetForm, updateTimeSheetForm, getLookUpPositions, getSitesByType, getCertificateTypeData, getAbsenceData, getAbsenceReasonData } from '../../apis/personnelApi';
import { getSiteDataByDistrictId, getSiteDataByOperation, fetchAllSites } from '../../apis/sitesApi';
import { ISchedularTable, IAttendanceForm } from '../../interface/Personnel';
import { fetchSchoolList } from '../../apis/schoolApi'
import { useAttandanceForm } from '../../hooks/personnel/attandance/useAttandanceForm'
import { ISchedularTimesheet } from '../../interface/scheduleTimesheet';
import { fetchAllDistrict } from '../../apis/districtsApi';
import { getUserData } from "../../utils/utils";
import moment from 'moment';
import Select, { components } from 'react-select';
import { formatDateBackendToUi } from '../../helper/date-helper';

const SiteSelectLoadingIndicator = (props: any) => (
    <components.LoadingIndicator {...props}>
        <span
            className="loader"
            style={{
                width: '22px',
                height: '22px',
                borderWidth: '3px',
                borderColor: '#ced4da',
                borderBottomColor: '#023047',
            }}
        />
    </components.LoadingIndicator>
);

const AttandanceModal: React.FC<{
    initialData?: IAttendanceForm;
    preloadedSchedules?: any[] | null;
    onClose: () => void;
    isEditingTimesheet: boolean;
    setIsEditingTimesheet: React.Dispatch<React.SetStateAction<boolean>>;
    personalId: number;
    attendanceID: number;
    refetch: any;
    schedular: ISchedularTable
    setSites: any,
    sites: any,
    encodedSiteNumbers: string;
}>
    = ({ initialData = {} as IAttendanceForm, preloadedSchedules = null, onClose, isEditingTimesheet, setIsEditingTimesheet, personalId, attendanceID, refetch, schedular, encodedSiteNumbers }) => {

        const [sites, setSites] = useState<any>([]);
        const [allSites, setAllSites] = useState<any[]>([]);
        const [personnelSites, setPersonnelSites] = useState<any[]>([]);
        const [position, setPosition] = useState<any>([]);
        const [absences, setAbsences] = useState<any>([]);
        const [absenceReasons, setAbsenceReasons] = useState<any>([]);
        const [multiRecords, setMultiRecords] = useState<IAttendanceForm[]>([]);
        const [isSubmittingAll, setIsSubmittingAll] = useState(false);
        const [initialDataLoading, setInitialDataLoading] = useState(true);
        const userData = getUserData();
        // Refs for React Select components
        const siteSelectRef = useRef<any>(null);
        const absentSelectRef = useRef<any>(null);
        const reasonSelectRef = useRef<any>(null);

        const normalizeRecordDate = (date: string | null) => {
            if (!date) return '';
            return moment(date).format('YYYY-MM-DD');
        };

        const isDuplicateRecord = (record: IAttendanceForm, existing: IAttendanceForm) => {
            return (
                normalizeRecordDate(record.date) === normalizeRecordDate(existing.date) &&
                record.siteNumber === existing.siteNumber &&
                String(record.scheduleId || '') === String(existing.scheduleId || '') &&
                record.fraction === existing.fraction &&
                record.reasonID === existing.reasonID &&
                record.paid === existing.paid &&
                record.charged === existing.charged &&
                (record.reason || '').trim() === (existing.reason || '').trim()
            );
        };

        // Add record to multi-record list
        const handleAddRecord = () => {
            if (!attendanceFormData.date || !attendanceFormData.siteNumber || !attendanceFormData.fraction || !attendanceFormData.reasonID) {
                const newErrors: any = {};
                if (!attendanceFormData.date) newErrors.date = { _errors: ['Date is required'] };
                if (!attendanceFormData.siteNumber) newErrors.siteNumber = { _errors: ['Site is required'] };
                if (!attendanceFormData.fraction) newErrors.fraction = { _errors: ['Absent type is required'] };
                if (!attendanceFormData.reasonID) newErrors.reasonID = { _errors: ['Reason is required'] };
                setErrors(newErrors);
                return;
            }

            const newRecord: IAttendanceForm = {
                ...attendanceFormData,
                attendanceID: 0,
                staffId: personalId,
                districtID: userData.districtID,
                scheduleId: attendanceFormData.scheduleId || ''
            };

            if (multiRecords.some((existing) => isDuplicateRecord(newRecord, existing))) {
                setErrors({ duplicate: { _errors: ['The record already exists.'] } } as any);
                return;
            }

            setMultiRecords([...multiRecords, newRecord]);
            setErrors(null);

            // Clear only date for the next entry; keep other fields
            void loadSchedulesForDate(null);
            setAttendanceFormData((prev) => ({
                ...prev,
                date: null,
            }));
        };

        // Remove record from list
        const handleRemoveRecord = (index: number) => {
            setMultiRecords(multiRecords.filter((_, i) => i !== index));
        };

        // Edit record from list
        const handleEditRecord = async (index: number) => {
            setErrors(null);
            const record = multiRecords[index];
            handleRemoveRecord(index);
            await loadSchedulesForDate(record.date);
            setAttendanceFormData(record);
        };

        // Submit all records
        const handleSubmitAll = async () => {
            if (multiRecords.length === 0) {
                return;
            }

            setIsSubmittingAll(true);
            try {
                const { createAttendanceForm } = await import('../../apis/personnelApi');
                // Submit all records in parallel
                const promises = multiRecords.map(record => createAttendanceForm(record));
                await Promise.all(promises);
                // Clear records and close modal
                setMultiRecords([]);
                refetch();
                onClose();
            } catch (error) {
                console.error('Error submitting records:', error);
            } finally {
                setIsSubmittingAll(false);
            }
        };
        const {
            attendanceFormData,
            setAttendanceFormData,
            handleChange,
            handleSubmit,
            errors,
            setErrors,
            loading,
            setDistrictSite,
            districtSite,
            schedules,
            setSchedules,
            schedulesLoading,
            loadSchedulesForDate,
        } = useAttandanceForm(initialData, setIsEditingTimesheet, isEditingTimesheet, attendanceID, onClose, refetch, personalId, sites, setSites, preloadedSchedules);

        const buildSitesFromSchedules = (scheduleList: any[]) => {
            if (!scheduleList?.length || !allSites?.length) {
                return [];
            }

            const matchingSites = scheduleList
                .map((schedule: any) => {
                    const matchingSite = allSites.find((site: any) => site.siteID === schedule.siteID);
                    if (!matchingSite) {
                        return null;
                    }
                    return {
                        ...matchingSite,
                        scheduleId: schedule.id,
                    };
                })
                .filter((site: any) => site !== null);

            return matchingSites?.length ? matchingSites : [];
        };

        const formatDate = (dateString: any) => {
            if (!dateString) return '';

            const date = new Date(dateString);
            const currentDate = moment(dateString).format('YYYY-MM-DD');
            return currentDate;
            // const year = date.getFullYear();
            // const month = String(date.getMonth() + 1).padStart(2, '0');
            // const day = String(date.getDate()).padStart(2, '0');

            // return `${year}-${month}-${day}`;
        };

        const sortOptions = (options: any[]) => {
            if (!options) return [];
            return [...options].sort((a: any, b: any) => (a.label || '').localeCompare(b.label || '', undefined, { numeric: true }));
        };

        const selectStyles = {
            control: (base: any, state: any) => ({
                ...base,
                minHeight: '38px',
                ...(state.isLoading
                    ? {
                        borderColor: '#023047',
                        boxShadow: '0 0 0 0.2rem rgba(2, 48, 71, 0.15)',
                    }
                    : {}),
            }),
            menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
            menu: (base: any) => ({ ...base, width: 'max-content', minWidth: '100%' }),
        };

        const handleSelectChange = (selectedOption: any, actionMeta: any) => {
            const { name } = actionMeta;
            handleChange({
                target: {
                    name,
                    value: selectedOption ? selectedOption.value : ''
                }
            } as any);
        };

        useEffect(() => {
            if (isEditingTimesheet) {
                setIsEditingTimesheet(true);
            }
        }, [isEditingTimesheet]);

        useEffect(() => {
            const fetchInitialData = async () => {
                setInitialDataLoading(true);
                try {
                    const [absenceDataResponse, absenceReasonDataResponse, allSitesResponse] = await Promise.all([
                        getAbsenceData(),
                        getAbsenceReasonData(),
                        fetchAllSites(),
                    ]);
                    setAbsences(absenceDataResponse?.data);
                    setAbsenceReasons(absenceReasonDataResponse?.data);
                    setAllSites(allSitesResponse?.data || []);

                    if (encodedSiteNumbers) {
                        const siteData = await getSiteDataByOperation(encodedSiteNumbers, 0);
                        setPersonnelSites(siteData || []);
                    } else {
                        setPersonnelSites([]);
                    }
                } catch (error) {
                    console.error("Error fetching data:", error);
                } finally {
                    setInitialDataLoading(false);
                }
            };
            fetchInitialData();
        }, []);

        useEffect(() => {
            if (!attendanceFormData.date) {
                setSites([]);
                return;
            }

            if (schedulesLoading || initialDataLoading) {
                setSites([]);
                return;
            }

            if (schedules === null) {
                setSites([]);
                return;
            }

            const availableSites = buildSitesFromSchedules(schedules);
            setSites(availableSites);

            if (!attendanceFormData.siteNumber || allSites.length === 0) {
                return;
            }

            const siteNumber = Number(attendanceFormData.siteNumber);
            const exactMatch = availableSites.find(
                (site: any) =>
                    Number(site.siteNumber) === siteNumber &&
                    Number(site.scheduleId) === Number(attendanceFormData.scheduleId || 0)
            );

            if (exactMatch) {
                return;
            }

            const sitesForNumber = availableSites.filter(
                (site: any) => Number(site.siteNumber) === siteNumber
            );

            if (sitesForNumber.length === 1) {
                setAttendanceFormData((prev) => ({
                    ...prev,
                    siteNumber: sitesForNumber[0].siteNumber,
                    siteName: sitesForNumber[0].siteName,
                    scheduleId: sitesForNumber[0].scheduleId,
                }));
                return;
            }

            if (sitesForNumber.length > 1 && attendanceFormData.siteName) {
                const matchByName = sitesForNumber.find(
                    (site: any) => site.siteName === attendanceFormData.siteName
                );
                if (matchByName) {
                    setAttendanceFormData((prev) => ({
                        ...prev,
                        siteNumber: matchByName.siteNumber,
                        siteName: matchByName.siteName,
                        scheduleId: matchByName.scheduleId,
                    }));
                    return;
                }
            }

            if (sitesForNumber.length === 0) {
                setAttendanceFormData((prev) => ({
                    ...prev,
                    siteNumber: 0,
                    siteName: '',
                    scheduleId: '',
                }));
            }
        }, [schedules, allSites, personnelSites, attendanceFormData.date, attendanceFormData.siteNumber, attendanceFormData.scheduleId, schedulesLoading, initialDataLoading]);



        const handleClose = () => {
            onClose()
        }

        const siteSelectOptions = sortOptions(
            sites?.map((site: any) => {
                const matchingSchedule = schedules?.find(
                    (schedule: any) => Number(schedule.id) === Number(site.scheduleId)
                );
                const isAdditional = !!matchingSchedule?.paycode;
                return {
                    value: `${site.siteNumber}|${site.scheduleId}`,
                    label: `${site.siteName} ${isAdditional ? '(Additional)' : ''}`,
                };
            }) || []
        );

        const isSiteOptionsLoading =
            Boolean(attendanceFormData.date) &&
            (schedulesLoading || initialDataLoading);

        const selectedSiteValue = (() => {
            if (!attendanceFormData.siteNumber) {
                return null;
            }

            const exactMatch = siteSelectOptions.find(
                (opt) =>
                    opt.value ===
                    `${attendanceFormData.siteNumber}|${attendanceFormData.scheduleId || ''}`
            );
            if (exactMatch) {
                return exactMatch;
            }

            return (
                siteSelectOptions.find((opt) => {
                    const [siteNum] = opt.value.split('|');
                    return Number(siteNum) === Number(attendanceFormData.siteNumber);
                }) || null
            );
        })();

        return (
        <>
            <div className={`modal fade show timesheetModal editAsignment align-items-lg-start overflow-auto align-items-md-start align-items-start justify-content-center`}
             id="Filter" aria-labelledby="FilterLabel" aria-hidden="true">
                <div className="modal-dialog modal-xl" style={{ maxWidth: '1170px', width: '90vw', minWidth: '1000px' }}>
                    <div className="modal-content p-4" style={{ width: '100%', maxWidth: '1170px' }}>
                        <div className="modal-body p-lg-2 p-md-2 p-0">
                            <div className="formCard mt-0 p-0 border-0">
                                <div className="FormTitleWithdivider position-relative d-flex align-items-center gap-3  mb-lg-4 mb-md-3 mb-3">
                                    <h3 className="text-nowrap formTitle m-0">{isEditingTimesheet ? 'Edit Attendance' : 'Attendance'}</h3>
                                    <hr className="w-100" />
                                    <div className="editForm" data-bs-dismiss="modal" aria-label="Close" onClick={handleClose} style={{ cursor: 'pointer' }}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
                                            <path d="M19 5.5L5 19.5M19 19.5L5 5.5" stroke="black" stroke-width="2" stroke-linecap="round"></path>
                                        </svg>
                                    </div>
                                </div>
                                <form className="DistrictForm">
                                    <div className="mb-3 row g-lg-4 g-md-4 g-3">
                                        <div className="col-lg-6 col-md-6 col-sm-6 col-12">
                                            <div className="mb-0 w-100">
                                                <label className="form-label mb-2">Date :</label>
                                                <input
                                                    type="date"
                                                    className="form-control"
                                                    id="date"
                                                    name="date"
                                                    value={formatDate(attendanceFormData.date)}
                                                    // max={new Date().toISOString().split("T")[0]}
                                                    onChange={(e) => {
                                                        handleChange(e)
                                                    }}
                                                />
                                                {errors?.date && (
                                                    <div className="text-danger">
                                                        {errors?.date?._errors[0]}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="col-lg-6 col-md-6 col-sm-6 col-12">
                                            <div className="mb-0 w-100">
                                                <label className="form-label mb-2">Sites Name:</label>
                                                <Select
                                                    ref={siteSelectRef}
                                                    name="siteNumber"
                                                    value={selectedSiteValue || null}
                                                    onChange={handleSelectChange}
                                                    options={siteSelectOptions}
                                                    isSearchable={true}
                                                    isClearable={!isSiteOptionsLoading}
                                                    isLoading={isSiteOptionsLoading}
                                                    components={{ LoadingIndicator: SiteSelectLoadingIndicator }}
                                                    isDisabled={
                                                        !attendanceFormData.date ||
                                                        isSiteOptionsLoading ||
                                                        sites.length === 0
                                                    }
                                                    placeholder={
                                                        !attendanceFormData.date
                                                            ? 'Select a date first'
                                                            : isSiteOptionsLoading
                                                                ? 'Loading sites...'
                                                                : sites.length === 0
                                                                    ? 'No sites available for this date'
                                                                    : 'Sites Name'
                                                    }
                                                    menuPortalTarget={document.body}
                                                    styles={selectStyles}
                                                />
                                                {errors?.siteNumber && (
                                                    <div className="text-danger">
                                                        {errors?.siteNumber?._errors[0]}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-lg-6 col-md-6 col-sm-6 col-12">
                                            <div className="mb-0 w-100">
                                                <label className="form-label mb-2">Absent :</label>
                                                <Select
                                                    ref={absentSelectRef}
                                                    name="fraction"
                                                    value={sortOptions(absences.map((abs: any) => ({ value: abs.absentName, label: abs.absentName }))).find(opt => opt.value === attendanceFormData.fraction)}
                                                    onChange={handleSelectChange}
                                                    options={sortOptions(absences.map((abs: any) => ({ value: abs.absentName, label: abs.absentName })))}
                                                    isSearchable={true}
                                                    isClearable={true}
                                                    placeholder="Select Absent"
                                                    menuPortalTarget={document.body}
                                                    styles={selectStyles}
                                                />
                                            </div>
                                        </div>

                                        <div className="col-lg-6 col-md-6 col-sm-6 col-12">
                                            <div className="mb-0 w-100">
                                                <label className="form-label mb-2">Reason :</label>
                                                <Select
                                                    ref={reasonSelectRef}
                                                    name="reasonID"
                                                    value={sortOptions(absenceReasons?.map((id: any) => ({ value: id.id, label: id.reason })) || []).find(opt => opt.value === attendanceFormData.reasonID)}
                                                    onChange={handleSelectChange}
                                                    options={sortOptions(absenceReasons?.map((id: any) => ({ value: id.id, label: id.reason })) || [])}
                                                    isSearchable={true}
                                                    isClearable={true}
                                                    placeholder="Select Reason"
                                                    menuPortalTarget={document.body}
                                                    styles={selectStyles}
                                                />
                                                {errors?.reasonID && (
                                                    <div className="text-danger">
                                                        {errors?.reasonID?._errors[0]}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="col-lg-6 col-md-6 col-sm-6 col-12 d-flex align-items-end">
                                            <div className="d-flex checked align-items-center gap-2">
                                                <div className="inputDesign position-relative">
                                                    <input
                                                        type="checkbox"
                                                        id="paid"
                                                        name="paid"
                                                        checked={attendanceFormData.paid}
                                                        onChange={(e) => {
                                                            handleChange(e)
                                                        }}
                                                    />
                                                    {errors?.paid && (
                                                        <div className="text-danger">
                                                            {errors?.paid?._errors[0]}
                                                        </div>
                                                    )}
                                                    <div className="checked">
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
                                                            <path d="M14.9999 10.5L10.5253 14.4999L8.99997 13.1365M19.9999 7.49998L19.9999 17.5C19.9999 19.1569 18.6568 20.5 16.9999 20.5H6.99998C5.34314 20.5 4 19.1569 4 17.5V7.49998C4 5.84314 5.34314 4.5 6.99998 4.5H16.9999C18.6568 4.5 19.9999 5.84314 19.9999 7.49998Z" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                    </div>
                                                    <div className="Unchecked">
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
                                                            <path d="M17 4.5C18.6569 4.5 20 5.84314 20 7.49998V17.5C20 19.1569 18.6569 20.5 17 20.5H7C5.34315 20.5 4 19.1569 4 17.5L4 7.49998C4 5.84314 5.34315 4.5 7 4.5L17 4.5Z" stroke="#A4A4A4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                    </div>
                                                </div>
                                                <label className="form-label m-0" style={{ fontWeight: attendanceFormData.paid ? 'bold' : 'normal' }}>Paid</label>
                                            </div>
                                        </div>

                                        <div className="col-lg-6 col-md-6 col-sm-6 col-12 d-flex align-items-end">
                                            <div className="d-flex checked align-items-center gap-2">
                                                <div className="inputDesign position-relative">
                                                    <input
                                                        type="checkbox"
                                                        id="charged"
                                                        name="charged"
                                                        checked={attendanceFormData.charged}
                                                        onChange={(e) => {
                                                            handleChange(e)
                                                        }}
                                                    />
                                                    {errors?.charged && (
                                                        <div className="text-danger">
                                                            {errors?.charged?._errors[0]}
                                                        </div>
                                                    )}
                                                    <div className="checked">
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
                                                            <path d="M14.9999 10.5L10.5253 14.4999L8.99997 13.1365M19.9999 7.49998L19.9999 17.5C19.9999 19.1569 18.6568 20.5 16.9999 20.5H6.99998C5.34314 20.5 4 19.1569 4 17.5V7.49998C4 5.84314 5.34314 4.5 6.99998 4.5H16.9999C18.6568 4.5 19.9999 5.84314 19.9999 7.49998Z" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                    </div>
                                                    <div className="Unchecked">
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
                                                            <path d="M17 4.5C18.6569 4.5 20 5.84314 20 7.49998V17.5C20 19.1569 18.6569 20.5 17 20.5H7C5.34315 20.5 4 19.1569 4 17.5L4 7.49998C4 5.84314 5.34315 4.5 7 4.5L17 4.5Z" stroke="#A4A4A4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                    </div>
                                                </div>
                                                <label className="form-label m-0" style={{ fontWeight: attendanceFormData.charged ? 'bold' : 'normal' }}>Charged</label>
                                            </div>
                                        </div>

                                        <div className='col-12'>
                                            <div className="mb-0 w-100">
                                                <label className="form-label mb-2">Notes :</label>
                                                <textarea className='w-100' rows={4} id="reason"
                                                    name="reason"
                                                    value={attendanceFormData.reason}
                                                    onChange={(e) => handleChange(e)} ></textarea>
                                                {errors?.reason && (
                                                    <div className="text-danger">
                                                        {errors?.reason?._errors[0]}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    {(errors as any)?.duplicate && (
                                        <div className="text-danger mb-3">
                                            {(errors as any).duplicate._errors[0]}
                                        </div>
                                    )}
                                    <div className="d-flex align-items-center justify-content-end gap-lg-4 gap-md-3 gap-3">
                                        <button type='button' onClick={handleClose} className="btn btn-transparent ps-3 pe-4">
                                            Cancel
                                        </button>
                                        
                                        {!isEditingTimesheet && (
                                            <button 
                                                type='button' 
                                                onClick={handleAddRecord} 
                                                className="btn btn-outline-primary ps-3 pe-4"
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="me-2" width="20" height="20" viewBox="0 0 24 24" fill="none">
                                                    <path d="M12 5V19M5 12H19" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                                </svg>
                                                Add
                                            </button>
                                        )}
                                        
                                        {multiRecords.length > 0 ? (
                                            <button 
                                                type='button' 
                                                onClick={handleSubmitAll} 
                                                className="btn btn-primary ps-3 pe-4" 
                                                disabled={isSubmittingAll}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                {isSubmittingAll ? (
                                                    <span className="btnloader loader"></span>
                                                ) : (
                                                    <>
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="me-2" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                            <path d="M13.8001 19.5514H19.8001M4.2002 19.5514L8.56618 18.6717C8.79796 18.625 9.01077 18.5109 9.17791 18.3437L18.9516 8.56461C19.4202 8.09576 19.4199 7.33577 18.9509 6.86731L16.8805 4.79923C16.4117 4.33097 15.6521 4.33129 15.1837 4.79995L5.40896 14.58C5.24214 14.7469 5.12824 14.9593 5.0815 15.1906L4.2002 19.5514Z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
                                                        </svg>
                                                        Submit All ({multiRecords.length})
                                                    </>
                                                )}
                                            </button>
                                        ) : (
                                            userData.role !== "Scope.AdminView" && userData.role !== "Scope.StudentService" && userData.role !== "Scope.PayrollService1" && userData.role !== "Scope.PayrollService2" && userData.role !== "Scope.StudentService_PayrollService1" && userData.role !== "Scope.StudentService_PayrollService1AND2" &&
                                            <button type='button' onClick={handleSubmit} className="btn btn-primary ps-3 pe-4" style={{ cursor: 'pointer' }}>
                                                {loading ? (
                                                    <span className="btnloader loader"></span>
                                                ) : (
                                                    <>
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="me-2" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                            <path d="M13.8001 19.5514H19.8001M4.2002 19.5514L8.56618 18.6717C8.79796 18.625 9.01077 18.5109 9.17791 18.3437L18.9516 8.56461C19.4202 8.09576 19.4199 7.33577 18.9509 6.86731L16.8805 4.79923C16.4117 4.33097 15.6521 4.33129 15.1837 4.79995L5.40896 14.58C5.24214 14.7469 5.12824 14.9593 5.0815 15.1906L4.2002 19.5514Z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
                                                        </svg>
                                                        <span>{isEditingTimesheet ? 'Save Changes' : 'Submit'}</span>
                                                    </>
                                                )}
                                            </button>
                                        )}
                                    </div>
                                </form>
                                
                                {/* Multi-Record List Display */}
                                {multiRecords.length > 0 && (
                                    <div className="mt-4">
                                        <h6 className="mb-3">Records to Submit ({multiRecords.length})</h6>
                                        <div className="table-responsive" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                            <table className="table table-striped table-hover">
                                                <thead className="sticky-top bg-light">
                                                    <tr>
                                                        <th>Date</th>
                                                        <th>Site</th>
                                                        <th>Absent</th>
                                                        <th>Reason</th>
                                                        <th>Paid</th>
                                                        <th>Charged</th>
                                                        <th>Notes</th>
                                                        <th>Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {multiRecords.map((record, index) => (
                                                        <tr key={index}>
                                                            <td>{record.date ? formatDateBackendToUi(record.date) : ''}</td>
                                                            <td>{record.siteName}</td>
                                                            <td>{record.fraction}</td>
                                                            <td>{absenceReasons.find((r: any) => r.id === record.reasonID)?.reason || ''}</td>
                                                            <td>
                                                                <span className={`badge ${record.paid ? 'bg-success' : 'bg-secondary'}`}>
                                                                    {record.paid ? 'Yes' : 'No'}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <span className={`badge ${record.charged ? 'bg-warning' : 'bg-secondary'}`}>
                                                                    {record.charged ? 'Yes' : 'No'}
                                                                </span>
                                                            </td>
                                                            <td>{record.reason?.substring(0, 30)}{record.reason?.length > 30 ? '...' : ''}</td>
                                                            <td>
                                                                <button 
                                                                    className="btn btn-sm btn-outline-primary me-1"
                                                                    onClick={() => handleEditRecord(index)}
                                                                    title="Edit"
                                                                >
                                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
                                                                        <path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                                                        <path d="M18.5 2.50001C18.8978 2.10218 19.4374 1.87868 20 1.87868C20.5626 1.87868 21.1022 2.10218 21.5 2.50001C21.8978 2.89784 22.1213 3.4374 22.1213 4.00001C22.1213 4.56262 21.8978 5.10218 21.5 5.50001L12 15L8 16L9 12L18.5 2.50001Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                                                    </svg>
                                                                </button>
                                                                <button 
                                                                    className="btn btn-sm btn-outline-danger"
                                                                    onClick={() => handleRemoveRecord(index)}
                                                                    title="Remove"
                                                                >
                                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
                                                                        <path d="M3 6H5H21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                                                        <path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                                                    </svg>
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
                    </>
    );
};

export default AttandanceModal;
